import { useCallback, useEffect, useRef, useState } from "react"

import { useBubbleTiptap } from "./Bubble"

const useBubbleMenuPosition = ({ editor }) => {
  const { popover } = useBubbleTiptap()
  const interactingRef = useRef(false)
  const hiddenByScrollRef = useRef(false)
  const toolbarRef = useRef(null)
  const [position, setPosition] = useState(null)
  const [visible, setVisible] = useState(false)

  const shouldShow = useCallback(currentEditor => {
    if (!currentEditor?.isEditable) return false
    if (hiddenByScrollRef.current) return false
    if (interactingRef.current) return true
    if (popover.current) return true
    if (!currentEditor.view.hasFocus()) return false
    const { from, to } = currentEditor.state.selection
    return from !== to
  }, [popover])

  const getSelectionRect = useCallback(currentEditor => {
    const { view } = currentEditor
    const selection = view.dom?.ownerDocument?.getSelection?.()

    if (selection?.rangeCount) {
      const rect = selection.getRangeAt(0).getBoundingClientRect()
      if (rect.width || rect.height) return rect
    }

    const { from } = currentEditor.state.selection
    const coords = view.coordsAtPos(from)
    return {
      left: coords.left,
      right: coords.right,
      top: coords.top,
      bottom: coords.bottom,
      width: Math.max(coords.right - coords.left, 0),
      height: Math.max(coords.bottom - coords.top, 0),
    }
  }, [])

  const getPosition = useCallback(
    currentEditor => getSelectionRect(currentEditor),
    [getSelectionRect],
  )

  const update = useCallback(() => {
    if (!editor) return
    if (!shouldShow(editor)) {
      setVisible(false)
      return
    }

    const nextPosition = getPosition(editor)
    setPosition(nextPosition)
    setVisible(true)
  }, [editor, shouldShow, getPosition])

  useEffect(() => {
    if (!editor) return
    const handleBlur = () => requestAnimationFrame(update)
    const handleSelectionUpdate = () => {
      hiddenByScrollRef.current = false
      update()
    }
    const handleIframeScroll = () => {
      interactingRef.current = false
      hiddenByScrollRef.current = true
      setVisible(false)
    }
    const handleIframePointerDown = () => {
      hiddenByScrollRef.current = false
      requestAnimationFrame(update)
    }
    const iframeDocument = editor.view.dom?.ownerDocument
    const iframeWindow = iframeDocument?.defaultView
    const iframeElement = iframeWindow?.frameElement

    update()
    editor.on('selectionUpdate', handleSelectionUpdate)
    editor.on('transaction', update)
    editor.on('focus', update)
    editor.on('blur', handleBlur)

    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    if (iframeDocument && iframeDocument !== window.document) {
      iframeDocument.addEventListener('scroll', handleIframeScroll, true)
      iframeWindow?.addEventListener('scroll', handleIframeScroll, true)
      iframeDocument.addEventListener('wheel', handleIframeScroll, true)
      iframeDocument.addEventListener('pointerdown', handleIframePointerDown, true)
      iframeElement?.addEventListener('scroll', handleIframeScroll, true)
    }

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate)
      editor.off('transaction', update)
      editor.off('focus', update)
      editor.off('blur', handleBlur)
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
      if (iframeDocument && iframeDocument !== window.document) {
        iframeDocument.removeEventListener('scroll', handleIframeScroll, true)
        iframeWindow?.removeEventListener('scroll', handleIframeScroll, true)
        iframeDocument.removeEventListener('wheel', handleIframeScroll, true)
        iframeDocument.removeEventListener('pointerdown', handleIframePointerDown, true)
        iframeElement?.removeEventListener('scroll', handleIframeScroll, true)
      }
    }
  }, [editor, update])

  useEffect(() => {
    if (!editor) return

    const handleOutsidePointerDown = event => {
      if (toolbarRef.current?.contains(event.target)) return
      if (popover.current) return
      interactingRef.current = false
      requestAnimationFrame(update)
    }

    const mainDocument = window.document
    const iframeDocument = editor.view.dom?.ownerDocument

    mainDocument.addEventListener('pointerdown', handleOutsidePointerDown, true)
    if (iframeDocument && iframeDocument !== mainDocument) {
      iframeDocument.addEventListener('pointerdown', handleOutsidePointerDown, true)
    }

    return () => {
      mainDocument.removeEventListener('pointerdown', handleOutsidePointerDown, true)
      if (iframeDocument && iframeDocument !== mainDocument) {
        iframeDocument.removeEventListener('pointerdown', handleOutsidePointerDown, true)
      }
    }
  }, [editor, popover, update])

  return { visible, position, toolbarRef, interactingRef, update }
}

export default useBubbleMenuPosition
